import { useCallback, useEffect, useState } from "react";
import type { Lang } from "../types.ts";
import type { LoadedCatalog } from "../catalog.ts";
import { DATA_FILES } from "../catalog.ts";
import { mediaUrl } from "../media.ts";
import { t } from "../i18n/ui.ts";
import { formatBytes, useServiceWorker } from "../state/useServiceWorker.ts";
import { formatVersion } from "../version.ts";
import { useVersion, reloadToLatest } from "../state/useVersion.ts";

interface Props {
  lang: Lang;
  catalog: LoadedCatalog;
}

type Precache =
  | { phase: "idle" }
  | { phase: "running"; done: number; total: number; failed: number }
  | { phase: "done"; done: number; total: number; failed: number };

/** Everything the app could hold offline: the catalogue and all question media. */
function offlineUrls(catalog: LoadedCatalog): string[] {
  const urls = new Set<string>([`/data/${DATA_FILES.catalog}`, `/data/${DATA_FILES.zh}`]);
  for (const q of catalog.questions) if (q.media) urls.add(mediaUrl(q.media));
  return [...urls];
}

export function SettingsView({ lang, catalog }: Props) {
  const offline = useServiceWorker();
  const version = useVersion();
  const [precache, setPrecache] = useState<Precache>({ phase: "idle" });

  const urls = offlineUrls(catalog);

  // Progress arrives from the service worker, which does the downloading so it
  // keeps going while you navigate around the app.
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onMessage = (event: MessageEvent) => {
      const data = event.data as
        | { type: string; done: number; total: number; failed: number }
        | undefined;
      if (!data) return;
      if (data.type === "precache-progress") {
        setPrecache({ phase: "running", done: data.done, total: data.total, failed: data.failed });
      } else if (data.type === "precache-done") {
        setPrecache({ phase: "done", done: data.done, total: data.total, failed: data.failed });
        void offline.refresh();
      }
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [offline]);

  const startPrecache = useCallback(async () => {
    const reg = await navigator.serviceWorker?.getRegistration();
    const worker = reg?.active;
    if (!worker) return;
    setPrecache({ phase: "running", done: 0, total: urls.length, failed: 0 });
    worker.postMessage({ type: "precache", urls });
  }, [urls]);

  const ready = offline.state === "active";

  return (
    <div className="settings">
      <h2 className="settings__title">{t(lang, "settings")}</h2>

      <section className="settings__group" aria-labelledby="set-offline">
        <h3 id="set-offline">{t(lang, "offlineSection")}</h3>

        <p className="settings__status">
          <span className={`drawer__dot drawer__dot--${offline.state}`} aria-hidden="true" />
          {t(lang, `sw_${offline.state}` as const)}
        </p>

        <dl className="settings__facts">
          <div>
            <dt>{t(lang, "cachedMedia")}</dt>
            <dd>
              {offline.cachedMedia} / {urls.length - 2}
            </dd>
          </div>
          {offline.usedBytes !== null && (
            <div>
              <dt>{t(lang, "storageUsed")}</dt>
              <dd>{formatBytes(offline.usedBytes)}</dd>
            </div>
          )}
        </dl>

        <p className="settings__hint">{t(lang, "precacheHint")}</p>

        {precache.phase === "running" ? (
          <>
            <progress
              className="settings__progress"
              value={precache.done}
              max={precache.total}
            />
            <p className="settings__hint">
              {t(lang, "precacheProgress", { done: precache.done, total: precache.total })}
            </p>
          </>
        ) : (
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => void startPrecache()}
            disabled={!ready}
          >
            {t(lang, "precacheStart")}
          </button>
        )}

        {precache.phase === "done" && (
          <p className="settings__hint" role="status">
            {t(lang, "precacheDone", { n: precache.done - precache.failed })}
            {precache.failed > 0 && ` · ${t(lang, "precacheFailed", { n: precache.failed })}`}
          </p>
        )}
      </section>

      <section className="settings__group" aria-labelledby="set-version">
        <h3 id="set-version">{t(lang, "version")}</h3>
        <p className="settings__status" translate="no">
          {formatVersion(version.current)}
        </p>
        {version.stale ? (
          <button type="button" className="btn" onClick={() => void reloadToLatest()}>
            {t(lang, "reloadNow")}
          </button>
        ) : (
          <p className="settings__hint">{t(lang, "versionCurrent")}</p>
        )}
      </section>
    </div>
  );
}
